import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

let activeCompanyId = null;
export const setActiveCompanyId = (id) => {
  activeCompanyId = id;
  if (id) localStorage.setItem("parapet_company_id", id);
  else localStorage.removeItem("parapet_company_id");
};
export const getActiveCompanyId = () => activeCompanyId || localStorage.getItem("parapet_company_id");

api.interceptors.request.use((config) => {
  const cid = getActiveCompanyId();
  if (cid) {
    config.headers["X-Company-Id"] = cid;
  }
  return config;
});

// Self-heal:
// (1) 401 Not authenticated → silently call /auth/refresh and retry the request once.
//     This prevents "Not authenticated" errors when the 15-min access token expires
//     mid-session (e.g., while a user is slowly filling out the onboarding form).
// (2) 403 "Not a member of this company" → wipe stale X-Company-Id and retry once.
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config;
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;

    // (1) Token expired or missing — try to refresh and retry.
    const isAuthEndpoint = cfg?.url?.includes("/auth/refresh") ||
                           cfg?.url?.includes("/auth/login") ||
                           cfg?.url?.includes("/auth/register");
    if (status === 401 && cfg && !cfg.__retriedAfterRefresh && !isAuthEndpoint) {
      cfg.__retriedAfterRefresh = true;
      try {
        await api.post("/auth/refresh");
        return api.request(cfg);
      } catch (e) {
        // Refresh failed — user really is logged out. Fall through to reject.
      }
    }

    // (2) Stale company id — drop it and retry once.
    if (
      status === 403 &&
      typeof detail === "string" &&
      detail.toLowerCase().includes("not a member") &&
      cfg &&
      !cfg.__retriedAfterStaleCompany
    ) {
      setActiveCompanyId(null);
      cfg.__retriedAfterStaleCompany = true;
      if (cfg.headers) delete cfg.headers["X-Company-Id"];
      return api.request(cfg);
    }
    return Promise.reject(error);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
